import type {
  FirmSignatureRequest,
  SignatureKind
} from "@/features/clientPortal/types";

/** "6, 7-8" -> [6, 7, 8], dropping anything outside the document. */
export const parsePages = (
  text: string,
  pageCount: number | null
): number[] => {
  const pages = new Set<number>();
  text
    .split(/[,\s]+/)
    .filter(Boolean)
    .forEach((part) => {
      const [a, b] = part.split("-").map((n) => Number.parseInt(n, 10));
      if (!Number.isFinite(a)) return;
      const end = Number.isFinite(b) && b >= a ? Math.min(b, a + 50) : a;
      for (let p = a; p <= end; p += 1) {
        if (p >= 1 && (pageCount === null || p <= pageCount)) pages.add(p);
      }
    });
  return [...pages].sort((x, y) => x - y);
};

export const formatPages = (pages: number[]): string => pages.join(", ");

/** "I-140 petition.pdf" -> "I-140 petition". */
export const defaultTitle = (label: string, filename: string): string =>
  (
    label.trim() ||
    filename
      .replace(/\.pdf$/i, "")
      .replace(/_+/g, " ")
      .replace(/\s+/g, " ")
  ).trim();

/** USCIS forms get ink; a form number in the name is the giveaway. */
export const suggestKind = (
  filename: string,
  detectedPages: number
): SignatureKind =>
  detectedPages > 0 || /\b(i|g|n|ar|ds)-?\d{2,4}[a-z]?\b/i.test(filename)
    ? "ink"
    : "esign";

const day = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Requests worth showing on Overview: open ones, and signed in the last 30 days. */
export const visibleRequests = (
  requests: FirmSignatureRequest[],
  now: Date = new Date()
): FirmSignatureRequest[] =>
  requests.filter((r) => {
    if (r.status === "cancelled") return false;
    if (r.status === "signed" || r.status === "completed") {
      const at = new Date(r.signed_at ?? r.reviewed?.at ?? 0).getTime();
      return now.getTime() - at < 30 * 86_400_000;
    }
    return true;
  });

export const needsReview = (r: FirmSignatureRequest): boolean =>
  r.status === "submitted";

/** The one line under the title, from the firm's side. */
export const statusLine = (r: FirmSignatureRequest, first: string): string => {
  switch (r.status) {
    case "upcoming":
      return `Not sent yet. ${first} sees it as coming up.`;
    case "pending":
      return r.returned_note
        ? `Sent back to ${first}: “${r.returned_note}”`
        : `Waiting for ${first} since ${day(r.opened_at ?? r.created_at)}`;
    case "submitted":
      return `${first} sent a photo of the signed ${
        r.pages.length === 1 ? "page" : "pages"
      }. Check it before filing.`;
    case "signed":
      return `Signed ${day(r.signed_at)}${
        r.signed ? ` · ${r.signed.method} as “${r.signed.name}”` : ""
      }`;
    case "completed":
      return `Ink signature accepted ${day(r.reviewed?.at ?? null)}`;
    default:
      return "";
  }
};

export const kindLabel = (r: Pick<FirmSignatureRequest, "kind" | "pages">) =>
  r.kind === "esign"
    ? "E-sign"
    : `Ink · ${r.pages.length === 1 ? "page" : "pages"} ${formatPages(
        r.pages
      )}`;
